// Unified admin sidebar layout — wraps any /admin/* child route.
// Groups admin routes into logical categories with active-state highlight + mobile drawer.
import React, { useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Menu, X, LayoutDashboard, Wallet, ShoppingBag, Gavel, Users, Settings, Sparkles, Radio, BarChart3, Bell, Puzzle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import AdminPendingBanner from './AdminPendingBanner';

const GROUPS = [
  {
    key: 'smart_panel', label: '🧠 Smart Panel', icon: Bell,
    items: [
      { to: '/admin/smart-panel', label: 'Anant Smart Panel (Unified)' },
      { to: '/admin/atc', label: '🏬 ATC Control Panel' },
      { to: '/admin/commission-ledger', label: '💰 Commission Ledger' },
      { to: '/admin/auto-approve', label: '⚡ Auto-Approve Rules' },
    ],
  },
  {
    key: 'ops', label: 'Operations', icon: LayoutDashboard,
    items: [
      { to: '/admin', label: 'Dashboard' },
      { to: '/admin/ops', label: '📊 Ops Dashboard (Live)' },
      { to: '/admin/marketplace-orders', label: '📦 Marketplace Orders' },
      { to: '/admin/orders', label: 'E-Shop Orders' },
      { to: '/admin/support', label: 'Support' },
      { to: '/admin/system-auditor', label: 'System Auditor' },
    ],
  },
  {
    key: 'wallets', label: 'Wallets & Tax', icon: Wallet, superOnly: false,
    items: [
      { to: '/admin/system-wallets', label: 'System Wallets' },
      { to: '/admin/tax-wallet', label: 'Tax Reserve (Super)', superOnly: true },
      { to: '/admin/ledger', label: 'Ledger' },
      { to: '/admin/fund-requests', label: 'Fund Requests' },
      { to: '/admin/payment-settings', label: 'Payments' },
      { to: '/admin/game-wallets', label: 'Game Wallets' },
    ],
  },
  {
    key: 'marketplace', label: 'Marketplace', icon: ShoppingBag,
    items: [
      { to: '/marketplace?tab=admin', label: 'Marketplace Console' },
      { to: '/admin/marketplace-reviews', label: '⭐ Reviews Management' },
      { to: '/admin/products', label: 'Products' },
      { to: '/admin/broadcasts', label: 'Broadcasts' },
      { to: '/admin/popups', label: 'Popups' },
      { to: '/admin/hot-news', label: 'Hot News' },
    ],
  },
  {
    key: 'delivery', label: 'Delivery', icon: Radio,
    items: [
      { to: '/admin/delivery-partners', label: '🛵 Approve Partners' },
      { to: '/delivery-partner', label: 'Partner Dashboard (self)' },
    ],
  },
  {
    key: 'auctions', label: 'Auctions', icon: Gavel,
    items: [
      { to: '/admin/auction', label: 'Auctions Admin' },
    ],
  },
  {
    key: 'users', label: 'Users & Access', icon: Users,
    items: [
      { to: '/admin/users', label: 'Users' },
      { to: '/admin/sub-admins', label: '🛡️ Sub-Admins (Super)', superOnly: true },
      { to: '/admin/activity-log', label: '📋 Activity Log' },
      { to: '/admin/user-tools', label: '👁️ Per-user Tool Visibility' },
      { to: '/admin/page-builder/user-permissions', label: 'Page Permissions' },
      { to: '/admin/2fa', label: '2FA' },
      { to: '/admin/franchise', label: 'Franchise' },
      { to: '/admin/affiliate', label: 'Affiliate' },
    ],
  },
  {
    key: 'activation_keys', label: '🔑 Activation Keys', icon: Puzzle,
    items: [
      { to: '/admin/activation-keys', label: 'Coupon / Key Generator' },
    ],
  },
  {
    key: 'growth', label: 'Growth & Analytics', icon: BarChart3,
    items: [
      { to: '/admin/share-analytics', label: 'Share Analytics' },
      { to: '/admin/tools-analytics', label: 'Tools Analytics' },
      { to: '/admin/marketing', label: 'Marketing' },
      { to: '/admin/rank-competitions', label: 'Rank Competitions' },
      { to: '/admin/ranks-rewards', label: 'Ranks & Rewards' },
    ],
  },
  {
    key: 'tools', label: 'Tools & AI', icon: Sparkles,
    items: [
      { to: '/admin/ai-control', label: 'AI Control' },
      { to: '/admin/ai-tools', label: 'AI Tools' },
      { to: '/admin/ai-usage', label: 'AI Usage' },
      { to: '/admin/voice-control', label: 'Voice Control' },
      { to: '/admin/whatsapp-bot', label: 'WhatsApp Bot' },
      { to: '/admin/free-tools', label: 'Free Tools' },
      { to: '/admin/supertool', label: 'Super Tool' },
    ],
  },
  {
    key: 'platform_settings', label: '⚙️ Platform', icon: Settings,
    items: [
      { to: '/admin/platform-settings', label: 'WhatsApp Channel / Group' },
      { to: '/admin/platform-content', label: '📄 Rules & Terms Content' },
    ],
  },
  {
    key: 'system', label: 'System', icon: Settings,
    items: [
      { to: '/admin/settings', label: 'Settings' },
      { to: '/admin/custom-pages', label: 'Custom Pages' },
      { to: '/admin/custom-tabs', label: 'Custom Tabs' },
      { to: '/admin/page-builder', label: 'Page Builder' },
      { to: '/admin/push-notifications', label: 'Push Notifications' },
      { to: '/admin/important-links', label: 'Important Links' },
      { to: '/admin/testimonials', label: 'Testimonials' },
    ],
  },
];

const isSuper = (email) => (email || '').toLowerCase() === 'freedomanil@gmail.com';

export default function AdminShell({ children }) {
  const [open, setOpen] = useState(false);
  const { user } = useAuth() || {};
  const superAdmin = isSuper(user?.email);
  const loc = useLocation();

  const filteredGroups = useMemo(() => {
    const userPerms = user?.admin_permissions || [];
    const hasFullAccess = superAdmin || userPerms.length === 0;   // super OR admin with no restrictions = full
    return GROUPS
      .filter(g => hasFullAccess || userPerms.includes(g.key))
      .map(g => ({
        ...g,
        items: (g.items || []).filter(it => !it.superOnly || superAdmin),
      }))
      .filter(g => g.items.length > 0);
  }, [superAdmin, user]);

  const Sidebar = () => (
    <aside className="w-64 shrink-0 h-full bg-slate-900/95 border-r border-slate-800 overflow-y-auto" data-testid="admin-sidebar">
      <div className="p-4 border-b border-slate-800">
        <div className="text-xs opacity-60">Admin Console</div>
        <div className="text-lg font-black">{superAdmin ? '👑 Super Admin' : '🛡️ Admin'}</div>
        <div className="text-[11px] opacity-70 mt-1 truncate">{user?.email}</div>
      </div>
      <nav className="p-2 space-y-3">
        {filteredGroups.map((g) => {
          const Icon = g.icon;
          return (
            <div key={g.key} data-testid={`admin-group-${g.key}`}>
              <div className="flex items-center gap-2 px-2 py-1 text-[10px] font-black uppercase opacity-70">
                <Icon size={12} /> {g.label}
              </div>
              <div className="space-y-0.5">
                {g.items.map((it) => (
                  <NavLink
                    key={it.to} to={it.to} end={it.to === '/admin'}
                    onClick={() => setOpen(false)}
                    data-testid={`admin-link-${it.to.replace(/\W+/g, '-')}`}
                    className={({ isActive }) => `block px-3 py-1.5 rounded-lg text-xs font-medium truncate ${
                      isActive ? 'bg-purple-500 text-white font-black' : 'text-slate-300 hover:bg-slate-800'
                    }`}>
                    {it.label}
                  </NavLink>
                ))}
              </div>
            </div>
          );
        })}
      </nav>
    </aside>
  );

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="admin-shell">
      {/* Pending Approvals banner — sits below fixed global Navbar (which is ~64-96px tall) */}
      <div className="pt-16 lg:pt-20">
        <AdminPendingBanner />
      </div>
      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-30 flex items-center gap-2 p-2 bg-slate-900 border-b border-slate-800">
        <button onClick={() => setOpen(true)} className="p-2 rounded-lg bg-slate-800" data-testid="admin-sidebar-toggle">
          <Menu size={18} />
        </button>
        <div className="text-sm font-black">Admin</div>
        <div className="ml-auto text-[10px] opacity-60 truncate max-w-[50%]">{loc.pathname}</div>
      </div>
      <div className="flex">
        {/* Desktop sidebar */}
        <div className="hidden lg:block sticky top-0 h-screen">
          <Sidebar />
        </div>
        {/* Mobile drawer */}
        {open && (
          <>
            <div onClick={() => setOpen(false)} className="fixed inset-0 bg-black/60 z-40 lg:hidden" data-testid="admin-drawer-backdrop" />
            <div className="fixed top-0 left-0 h-full z-50 lg:hidden">
              <div className="relative h-full">
                <button onClick={() => setOpen(false)} className="absolute top-2 right-2 p-2 rounded-lg bg-slate-800 z-10" data-testid="admin-drawer-close">
                  <X size={16} />
                </button>
                <Sidebar />
              </div>
            </div>
          </>
        )}
        <main className="flex-1 min-w-0">
          {children}
        </main>
      </div>
    </div>
  );
}
